import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import * as clientChecklistActions from './client-report-checklist-actions';
import clientChecklistService from './client-report-checklist-service';
import { formatDateTime, formatMessage } from '~/i18n/translate';
import { saveAs } from 'file-saver';

import {
  GET_ALL_SELECTS_REQUEST,
  GET_LIST_ALL_CHECKLIST_REPORTS_REQUEST,
  GET_MODAL_QUESTIONS_REQUEST,
  SHARE_CSV
} from './client-report-checklist-constants';
import { ISA_ERROR } from '~/constants';
import { AppStateType } from '../root-reducer';
import notification from '~/services/notification';

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAllChecklistReports({
  type,
  payload
}: {
  type: any;
  payload: {
    inactive?: string;
    vendors?: any[];
    date?: any[];
    clients?: any[];
  };
}) {
  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(clientChecklistActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select(
      (state: AppStateType) => state.clientReportsChecklist.list
    );

    const params = {
      inactive: payload?.inactive,
      orderAsc: order === 'asc' ? true : 'asc',
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data: clientChecklist }, { data: count }] = yield all([
      call(
        clientChecklistService.getListAllReportsChecklist,
        {
          ...params
        },
        {
          cancelToken
        }
      ),
      call(
        clientChecklistService.getListCountReportsChecklist,
        {
          ...params
        },
        {
          cancelToken
        }
      )
    ]);

    let sellers = [];

    if (clientChecklist?.length > 0) {
      const { data } = yield call(clientChecklistService.getSellers, {
        userExternalCodes: clientChecklist?.map(item => item?.userExternalCode)?.join(',')
      });

      sellers = data;
    }

    const parsedData = clientChecklist?.map(item => ({
      ...item,
      user: sellers?.find(seller => seller?.externalCode === item?.userExternalCode)
    }));

    yield put(
      clientChecklistActions.getAllReportsChecklistSuccess({
        checklistReport: parsedData,
        rowsCount: count.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientChecklistActions.setLoadingData(false));
  }
}

export function* getAllSelects({ payload }: any) {
  yield delay(200);

  try {
    yield put(clientChecklistActions.setLoadingSelects(true));

    const { externalCode } = yield select(state => state.auth.user);

    const filters = {
      clients: payload?.clients ?? '',
      vendors: payload?.vendors ?? ''
    };

    const [{ data: allUsers }, { data: allClients }] = yield all([
      call(clientChecklistService.getSellers, {
        queryString: filters?.vendors
      }),
      call(clientChecklistService.getClients, {
        userExternalCode: externalCode,
        queryString: filters?.clients
      })
    ]);

    yield put(
      clientChecklistActions.getAllSelectsSuccess({
        vendors: allUsers,
        clients: allClients
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientChecklistActions.setLoadingSelects(false));
  }
}

export function* getModalQuestions({ payload }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  const { rowsPerPage, page, order, orderBy } = yield select(
    (state: AppStateType) => state.clientReportsChecklist.modalData.list
  );

  const params = {
    usersExternalCodes: payload?.vendors?.map(vendor => vendor?.externalCode)?.join(',') ?? '',
    clientsExternalCodes: payload?.clients?.map(client => client?.externalCode)?.join(',') ?? '',
    initialDate: payload?.date
      ? payload?.date[0]?.toISOString()
      : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00'),
    finalDate: payload?.date
      ? payload?.date[1]?.toISOString()
      : new Date(new Date().toISOString().split('T')[0] + 'T00:00:00'),
    skip: page === 0 ? 0 : page * rowsPerPage,
    limit: rowsPerPage,
    order,
    orderBy,
    questionaryId: payload?.questionaryId
  };

  try {
    const [{ data: answers }, { data: count }] = yield all([
      call(clientChecklistService.getAnswers as any, {
        ...params
      }),
      call(clientChecklistService.getAnswers as any, {
        ...params,
        isCount: true
      })
    ]);

    let sellers = [];

    if (answers?.length > 0) {
      const { data } = yield call(clientChecklistService.getSellers, {
        userExternalCodes: answers?.map(item => item?.userExternalCode)?.join(',')
      });

      sellers = data;
    }

    const parsedData = answers?.map(item => ({
      ...item,
      user: sellers?.find(seller => seller?.externalCode === item?.userExternalCode)
    }));

    yield put(
      clientChecklistActions.getModalQuestionsSuccess({
        answersData: parsedData,
        count: count.count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export function* shareCsv({ exportType, filters }: any) {
  const toast: any = notification.loading(formatMessage('commonLoadingMessage'));

  try {
    const params = {
      inactive: filters?.inactive
    };

    if (exportType === 'xlsx') {
      const { data: xlsx } = yield call(clientChecklistService.getXls, {
        ...params,
        xlsx: true
      });

      const blob = new Blob([xlsx], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `Questionário - ${formatDateTime(new Date())}.xlsx`);
    } else {
      const { data: csv } = yield call(clientChecklistService.getCSV, {
        ...params,
        csv: true
      });

      const reader = new FileReader();

      const blobFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      reader.readAsDataURL(blobFile);
      reader.onloadend = async function () {
        saveAs(blobFile, `Questionário - ${formatDateTime(new Date())}.csv`);
      };
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    toast.close();
  }
}

export default all([
  takeLatest(GET_LIST_ALL_CHECKLIST_REPORTS_REQUEST, getAllChecklistReports),
  takeLatest(GET_MODAL_QUESTIONS_REQUEST, getModalQuestions),
  takeLatest(GET_ALL_SELECTS_REQUEST, getAllSelects),
  takeLatest(SHARE_CSV, shareCsv)
]);
